import {concat, isUndefined} from 'lodash';
import React, {Fragment} from 'react';
import styled from 'styled-components';

import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {DropdownItemChannelIcon} from '../../../components/common/dropdowns/dropdownItemChannelIcon';
import {BaseFilterDropdownProps} from '../../../components/common/filters/common/types';
import {
  FilterRowGenerator,
  ResourceMultiSelectFilterDropdown,
} from '../../../components/common/filters/resourceMultiSelectFilterDropdown';
import {computeFilterDropdownSourceSync} from '../../../components/common/filters/sourceFilter/filterDropdownSource';
import {getChannelIconName} from '../../../helpers/channelViewHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {AnalyticsFilterTypesEnum} from '../../../models/api/analyticsSavedViewModel';
import {ChannelModel} from '../../../models/api/channelModel';
import {
  getAccessibleSharedChannels,
  getChannels,
  getUserPrivateChannels,
} from '../../../store/data/channels/channelsSelectors';
import {getAnalyticsSelectedNamespace} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {makeFilterRows} from '../../helpers/analyticsFiltersHelpers';
import {useAnalyticsFilter} from '../../helpers/hooks/useAnalyticsFilter';
import {AnalyticsFilterButtonValue} from './common/analyticsFilterButtonValue';
import {analyticsFiltersConfig} from './common/analyticsFiltersConfig';
import {AnalyticsFilterItem, renderFilterTitle, useFiltersTracking} from './common/helpers';

/*
 * Constants.
 */

const {name, iconName, activeIconName} = analyticsFiltersConfig[AnalyticsFilterTypesEnum.CHANNELS];

/*
 * Style.
 */

const StyledInlineIcon = styled(Icon)`
  display: inline-block;
  margin-right: 4px;
  vertical-align: middle;
`;

/*
 * Hook.
 */

export const useAnalyticsChannelsFilter = (): AnalyticsFilterItem => {
  const [selectedItemIds, onUpdate] = useAnalyticsFilter(AnalyticsFilterTypesEnum.CHANNELS);
  const {trackOnEdit, trackOnRemove} = useFiltersTracking(AnalyticsFilterTypesEnum.CHANNELS);

  const allChannels = useAppSelector(getChannels);
  const namespace = useAppSelector(getAnalyticsSelectedNamespace);
  const isIndividualReport = isTeammateNamespace(namespace);
  const privateChannels = useAppSelector(getUserPrivateChannels);
  const sharedChannels = useAppSelector(getAccessibleSharedChannels);

  const channels = concat(sharedChannels, isIndividualReport ? privateChannels : []);

  const onSelectItems = (updatedSelectedItems: ReadonlyArray<number>) => {
    onUpdate(updatedSelectedItems);
    trackOnEdit(updatedSelectedItems, selectedItemIds);
  };

  const isActive = !isUndefined(selectedItemIds) && selectedItemIds.length > 0;

  const renderButton = () => (
    <AnalyticsFilterButtonValue
      selectedIds={selectedItemIds}
      itemsMap={allChannels}
      iconName={isActive ? activeIconName : iconName}
      renderItem={renderButtonItem}
      label={name}
      isActive={isActive}
    />
  );

  const dropdownSource = computeFilterDropdownSourceSync(channels, filterRows);
  const renderDropdown = (dropdownProps: BaseFilterDropdownProps) => (
    <ResourceMultiSelectFilterDropdown
      {...dropdownProps}
      dropdownTitle={name}
      selectedItemIds={selectedItemIds}
      onSetItems={onSelectItems}
      dropdownSource={dropdownSource}
      computeFilterGenerator={computeFilterGenerator}
      onClearAll={trackOnRemove}
    />
  );

  return {
    id: 'channels-filter',
    type: AnalyticsFilterTypesEnum.CHANNELS,
    isActive,
    title: renderFilterTitle(name, iconName),
    renderDropdown,
    renderButton,
  };
};

/*
 * Helpers.
 */

const computeFilterGenerator: FilterRowGenerator<ChannelModel> = (
  item,
  dropdownSelectedIds,
  makeOnItemSelect,
) => {
  const isChecked = dropdownSelectedIds.includes(item.id);

  return {
    renderer: () => (
      <>
        <DropdownItemChannelIcon channel={item} />
        <DropdownItemContent isChecked={isChecked}>{item.displayName}</DropdownItemContent>
      </>
    ),
    onSelect: makeOnItemSelect(item.id),
    isSelected: isChecked,
  };
};

const filterRows = makeFilterRows<ChannelModel>((item) => item.displayName);

const renderButtonItem = (channel: ChannelModel) => (
  <UserThemeConsumer>
    {(theme) => (
      <Fragment key={channel.id}>
        <StyledInlineIcon name={getChannelIconName(channel.contactSource)} color={theme.greys.shade50} />
        {channel.displayName}
      </Fragment>
    )}
  </UserThemeConsumer>
);
